import React from 'react';

const floatingStocks = [
  { symbol: 'sAAPL', logo: 'AAPL', price: '189.45', change: '+2.34%', positive: true, delay: 0 },
  { symbol: 'sNVDA', logo: 'NVDA', price: '875.32', change: '+4.12%', positive: true, delay: 2 },
  { symbol: 'sTSLA', logo: 'TSLA', price: '245.67', change: '-1.23%', positive: false, delay: 4 },
  { symbol: 'sMSFT', logo: 'MSFT', price: '378.91', change: '+1.87%', positive: true, delay: 1 },
  { symbol: 'sGOOGL', logo: 'GOOGL', price: '141.23', change: '+0.95%', positive: true, delay: 3 },
  { symbol: 'sMETA', logo: 'META', price: '485.12', change: '+3.21%', positive: true, delay: 5 },
  { symbol: 'sAMZN', logo: 'AMZN', price: '178.34', change: '+1.45%', positive: true, delay: 2.5 },
  { symbol: 'sJPM', logo: 'JPM', price: '195.67', change: '-0.87%', positive: false, delay: 3.5 },
];

const FloatingStockCard = ({ stock, position }) => {
  const logoUrl = `https://assets-sir.kraken.com/equities/logos/v1/${stock.logo}.png?i=kds`;
  
  return (
    <div 
      className={`absolute ${position} bg-white/[0.05] backdrop-blur-sm border border-white/10 rounded-xl px-4 py-3 animate-float opacity-50 hover:opacity-80 transition-opacity`}
      style={{ 
        animationDelay: `${stock.delay}s`,
        animationDuration: '8s'
      }}
    >
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full overflow-hidden bg-white/10 flex-shrink-0">
          <img 
            src={logoUrl} 
            alt={stock.symbol}
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div>
          <div className="text-white text-sm font-medium">{stock.symbol}</div>
          <div className="flex items-center gap-2">
            <span className="text-gray-400 text-xs">${stock.price}</span>
            <span className={`text-xs ${stock.positive ? 'text-green-400' : 'text-red-400'}`}>
              {stock.change}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const ChartLine = ({ className, delay }) => (
  <svg 
    className={`absolute ${className} opacity-20`} 
    width="200" 
    height="60" 
    viewBox="0 0 200 60"
    style={{ animationDelay: `${delay}s` }}
  >
    <path
      d="M0 40 Q20 35, 40 38 T80 30 T120 35 T160 20 T200 25"
      fill="none"
      stroke="url(#chartGradient)"
      strokeWidth="2"
      className="animate-draw"
    />
    <defs>
      <linearGradient id="chartGradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#7B61FF" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#22C55E" stopOpacity="0.6" />
      </linearGradient>
    </defs>
  </svg>
);

const Hero = () => {
  const positions = [
    'top-32 left-[5%]',
    'top-48 right-[8%]',
    'top-64 left-[12%]',
    'bottom-32 right-[5%]',
    'bottom-48 left-[8%]',
    'top-40 right-[15%]',
    'bottom-40 right-[12%]',
    'top-56 left-[3%]',
  ];

  return (
    <section className="relative pt-32 pb-20 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#1a0533] via-[#0b0014] to-[#0b0014]" />
      
      {/* Animated gradient orbs */}
      <div className="absolute top-20 left-1/4 w-[600px] h-[600px] bg-purple-600/20 rounded-full blur-[120px] animate-pulse" />
      <div className="absolute top-40 right-1/4 w-[400px] h-[400px] bg-indigo-600/15 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
      
      {/* Floating Stock Cards - Hidden on mobile for cleaner look */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
        {floatingStocks.map((stock, index) => (
          <FloatingStockCard 
            key={stock.symbol} 
            stock={stock} 
            position={positions[index]}
          />
        ))}
      </div>
      
      {/* Animated Chart Lines */}
      <ChartLine className="top-36 left-[20%] rotate-[-5deg]" delay={0} />
      <ChartLine className="top-52 right-[18%] rotate-[3deg]" delay={1.5} />
      <ChartLine className="bottom-36 left-[15%] rotate-[8deg]" delay={3} />
      <ChartLine className="bottom-48 right-[22%] rotate-[-3deg]" delay={2} />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute inset-0" style={{
          backgroundImage: `linear-gradient(rgba(123, 97, 255, 0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(123, 97, 255, 0.5) 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
      </div>
      
      <div className="relative max-w-[1400px] mx-auto px-6">
        {/* Stocks Protocol Logo */}
        <div className="flex justify-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_stock-mirrors/artifacts/gdgmfuzc_2.%20Logo%202.png" 
            alt="Stocks Protocol" 
            className="h-16 md:h-24 w-auto object-contain"
          />
        </div>

        {/* Main Headline */}
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold text-white leading-tight mb-6">
            The Layer 1 Blockchain for Tokenized Stocks
          </h1>
          
          <p className="text-lg md:text-xl lg:text-2xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Built on Stellar and XRPL for lightning-fast settlements and near-zero fees.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <a 
              href="/signup" 
              className="w-full sm:w-auto px-8 py-4 text-lg font-semibold text-white bg-[#7B61FF] hover:bg-[#6B4FFF] rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/25"
            >
              Start Trading
            </a>
            <a 
              href="/explorer" 
              className="w-full sm:w-auto px-8 py-4 text-lg font-semibold text-white border border-white/20 hover:border-purple-500/50 hover:bg-white/5 rounded-lg transition-all duration-200"
            >
              Explorer
            </a>
          </div>

          {/* Built on */}
          <div className="flex flex-col items-center gap-4">
            <p className="text-sm text-gray-500">Built on</p>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-5 py-2.5 bg-white/5 border border-white/10 rounded-xl">
                <img src="/stellar-logo.svg" alt="Stellar" className="w-8 h-8" />
                <span className="text-white font-medium">Stellar</span>
              </div>
              <div className="flex items-center gap-2 px-5 py-2.5 bg-white/5 border border-white/10 rounded-xl">
                <img src="/xrpl-logo.svg" alt="XRPL" className="w-8 h-8" />
                <span className="text-white font-medium">XRPL</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
